import { useTranslations } from "next-intl";

export type Destination = {
  name: string;
  image: string;
  description: string;
  hotels: number;
  region?: string;
  id: string;
  country: string;
  city: string;
  type: string;
};

export const getDestinationsByRegion = (
  t: ReturnType<typeof useTranslations>
) => ({
  // 1. EUROPE (6)
  europe: [
    {
      name: t("Destinations.Barcelona"),
      image: "/assets/v2/images/barcelona-destination.jpg",
      description: t("Destinations.BarcelonaDescription"),
      hotels: 145,
      region: "Europe",
      id: "C27790",
      country: "Spain",
      city: "Barcelona",
      type: "City",
    },
    {
      name: t("Destinations.Edinburgh"),
      image: "/assets/v2/images/edinburgh-destination.jpg",
      description: t("Destinations.EdinburghDescription"),
      hotels: 125,
      region: "Europe",
      id: "C37089",
      country: "United Kingdom",
      city: "Edinburgh",
      type: "City",
    },
    {
      name: t("Destinations.Lisbon"),
      image: "/assets/v2/images/lisbon-destination.jpg",
      description: t("Destinations.LisbonDescription"),
      hotels: 105,
      region: "Europe",
      id: "C97303",
      country: "Portugal",
      city: "Lisbon",
      type: "City",
    },
    {
      name: t("Destinations.London"),
      image: "/assets/v2/images/london-destination.jpg",
      description: t("Destinations.LondonDescription"),
      hotels: 350,
      region: "Europe",
      id: "C35155",
      country: "United Kingdom",
      city: "London",
      type: "City",
    },
    {
      name: t("Destinations.Madrid"),
      image: "/assets/v2/images/madrid-destination.jpg",
      description: t("Destinations.MadridDescription"),
      hotels: 140,
      region: "Europe",
      id: "C31362",
      destination: "Madrid & surrounding area",
      country: "Spain",
      city: "Madrid",
      label: "Madrid & surrounding area",
      type: "City",
    },
    {
      name: t("Destinations.Porto"),
      image: "/assets/v2/images/porto-destination.jpg",
      description: t("Destinations.PortoDescription"),
      hotels: 95,
      region: "Europe",
      id: "C82171",
      country: "Portugal",
      city: "Porto",
      type: "City",
    },
  ],

  // 2. ASIA (6)
  asia: [
    {
      name: t("Destinations.Osaka"),
      image: "/assets/v2/images/osaka-destination.jpg",
      description: t("Destinations.OsakaDescription"),
      hotels: 160,
      region: "Asia",
      id: "C63546",
      country: "Japan",
      city: "Osaka",
      type: "City",
    },
    {
      name: t("Destinations.Singapore"),
      image: "/assets/v2/images/singapore-destination.jpg",
      description: t("Destinations.SingaporeDescription"),
      hotels: 200,
      region: "Asia",
      id: "R987",
      country: "Singapore",
      city: "Singapore",
      type: "Region",
    },
    {
      name: t("Destinations.Bangkok"),
      image: "/assets/v2/images/bangkok-destination.jpg",
      description: t("Destinations.BangkokDescription"),
      hotels: 240,
      region: "Asia",
      id: "C88219",
      country: "Thailand",
      city: "Bangkok",
      type: "City",
    },
    {
      name: t("Destinations.Seoul"),
      image: "/assets/v2/images/seoul-destination.jpg",
      description: t("Destinations.SeoulDescription"),
      hotels: 180,
      region: "Asia",
      id: "C70959",
      country: "Korea",
      city: "Seoul",
      type: "City",
    },
    {
      name: t("Destinations.Bali"),
      image: "/assets/v2/images/bali-destination.jpg",
      description: t("Destinations.BaliDescription"),
      hotels: 320,
      region: "Asia",
      id: "C55015",
      country: "Indonesia",
      city: "Balikpapan",
      type: "City",
    },
    {
      name: t("Destinations.Tokyo"),
      image: "/assets/v2/images/tokyo-destination.jpg",
      description: t("Destinations.TokyoDescription"),
      hotels: 280,
      region: "Asia",
      id: "C64219",
      country: "Japan",
      city: "Tokyo",
      type: "City",
    },
  ],

  // 3. NORTH AMERICA (6)
  northAmerica: [
    {
      name: t("Destinations.LosAngeles"),
      image: "/assets/v2/images/los-angeles-destination.jpg",
      description: t("Destinations.LosAngelesDescription"),
      hotels: 230,
      region: "North America",
      id: "C91525",
      country: "USA",
      city: "Los Angeles",
      type: "City",
    },
    {
      name: t("Destinations.NewYork"),
      image: "/assets/v2/images/new-york.jpg",
      description: t("Destinations.NewYorkDescription"),
      hotels: 400,
      region: "North America",
      id: "C96604",
      country: "USA",
      city: "New York",
      type: "City",
    },
    {
      name: t("Destinations.Toronto"),
      image: "/assets/v2/images/toronto-destination.jpg",
      description: t("Destinations.TorontoDescription"),
      hotels: 150,
      region: "North America",
      id: "C11342",
      country: "Canada",
      city: "Toronto",
      type: "City",
    },
    {
      name: t("Destinations.Cancun"),
      image: "/assets/v2/images/cancun-destination.jpg",
      description: t("Destinations.CancunDescription"),
      hotels: 210,
      region: "North America",
      id: "C73937",
      country: "Mexico",
      city: "Cancun",
      type: "City",
    },
    {
      name: t("Destinations.Chicago"),
      image: "/assets/v2/images/chicago-destination.jpg",
      description: t("Destinations.ChicagoDescription"),
      hotels: 190,
      region: "North America",
      id: "C93104",
      country: "USA",
      city: "Chicago",
      type: "City",
    },
    {
      name: t("Destinations.Vancouver"),
      image: "/assets/v2/images/vancouver-destination.jpg",
      description: t("Destinations.VancouverDescription"),
      hotels: 175,
      region: "North America",
      id: "C10484",
      country: "Canada",
      city: "Vancouver",
      type: "City",
    },
  ],

  // 4. SOUTH AMERICA (6)
  southAmerica: [
    {
      name: t("Destinations.RioDeJaneiro"),
      image: "/assets/v2/images/rio-de-janeiro-destination.jpg",
      description: t("Destinations.RioDeJaneiroDescription"),
      hotels: 175,
      region: "South America",
      id: "C9375",
      country: "Brazil",
      city: "Rio de Janeiro",
      type: "City",
    },
    {
      name: t("Destinations.BuenosAires"),
      image: "/assets/v2/images/buenos-aires-destination.jpg",
      description: t("Destinations.BuenosAiresDescription"),
      hotels: 130,
      region: "South America",
      id: "C209",
      country: "Argentina",
      city: "Buenos Aires",
      type: "City",
    },
    {
      name: t("Destinations.MachuPicchu"),
      image: "/assets/v2/images/machu-picchu-destination.jpg",
      description: t("Destinations.MachuPicchuDescription"),
      hotels: 45,
      region: "South America",
      id: "C78596",
      country: "Peru",
      city: "Machu Picchu",
      type: "City",
    },
    {
      name: t("Destinations.Cartagena"),
      image: "/assets/v2/images/cartagena-destination.jpg",
      description: t("Destinations.CartagenaDescription"),
      hotels: 90,
      region: "South America",
      id: "C16132",
      country: "Colombia",
      city: "Cartagena",
      type: "City",
    },
    {
      name: t("Destinations.Patagonia"),
      image: "/assets/v2/images/patagonia-destination.jpg",
      description: t("Destinations.PatagoniaDescription"),
      hotels: 60,
      region: "South America",
      id: "C90709",
      country: "USA",
      city: "Patagonia",
      type: "City",
    },
    {
      name: t("Destinations.Galapagos"),
      image: "/assets/v2/images/galapagos-destination.jpg",
      description: t("Destinations.GalapagosDescription"),
      hotels: 55,
      region: "South America",
      id: "C26161",
      country: "Ecuador",
      city: "Galápagos",
      type: "City",
    },
  ],

  // 5. AFRICA (6)
  africa: [
    {
      name: t("Destinations.CapeTown"),
      image: "/assets/v2/images/cape-town-destination.jpg",
      description: t("Destinations.CapeTownDescription"),
      hotels: 180,
      region: "Africa",
      id: "C101101",
      country: "South Africa",
      city: "Cape Town",
      type: "City",
    },
    {
      name: t("Destinations.Marrakech"),
      image: "/assets/v2/images/marrakech-destination.jpg",
      description: t("Destinations.MarrakechDescription"),
      hotels: 120,
      region: "Africa",
      id: "C72495",
      country: "Morocco",
      city: "Marrakech",
      type: "City",
    },

    {
      name: t("Destinations.Zanzibar"),
      image: "/assets/v2/images/zanzibar-destination.jpg",
      description: t("Destinations.ZanzibarDescription"),
      hotels: 65,
      region: "Africa",
      id: "C90336",
      country: "Tanzania",
      city: "Zanzibar City",
      type: "City",
    },
    {
      name: t("Destinations.VictoriaFalls"),
      image: "/assets/v2/images/victoria-falls-destination.jpg",
      description: t("Destinations.VictoriaFallsDescription"),
      hotels: 50,
      region: "Africa",
      id: "C101921",
      country: "Zimbabwe",
      city: "Victoria Falls",
      type: "City",
    },
    {
      name: t("Destinations.Cairo"),
      image: "/assets/v2/images/cairo-destination.jpg",
      description: t("Destinations.CairoDescription"),
      hotels: 140,
      region: "Africa",
      id: "C26547",
      country: "Egypt",
      city: "Cairo",
      type: "City",
    },
  ],

  // 6. OCEANIA (6)
  oceania: [
    {
      name: t("Destinations.Brisbane"),
      image: "/assets/v2/images/brisbane-destination.jpg",
      description: t("Destinations.BrisbaneDescription"),
      hotels: 100,
      region: "Oceania",
      id: "C3223",
      country: "Australia",
      city: "Brisbane",
      type: "City",
    },
    {
      name: t("Destinations.Sydney"),
      image: "/assets/v2/images/sydney-destination.jpg",
      description: t("Destinations.SydneyDescription"),
      hotels: 220,
      region: "Oceania",
      id: "C2637",
      country: "Australia",
      city: "Sydney",
      type: "City",
    },
    {
      name: t("Destinations.Auckland"),
      image: "/assets/v2/images/auckland-destination.jpg",
      description: t("Destinations.AucklandDescription"),
      hotels: 130,
      region: "Oceania",
      id: "C77502",
      country: "New Zealand",
      city: "Auckland",
      type: "City",
    },
    {
      name: t("Destinations.Queenstown"),
      image: "/assets/v2/images/queenstown-destination.jpg",
      description: t("Destinations.QueenstownDescription"),
      hotels: 85,
      region: "Oceania",
      id: "C78064",
      country: "New Zealand",
      city: "Queenstown",
      type: "City",
    },
    {
      name: t("Destinations.Fiji"),
      image: "/assets/v2/images/fiji-destination.jpg",
      description: t("Destinations.FijiDescription"),
      hotels: 70,
      region: "Oceania",
      id: "R367",
      country: "Fiji",
      city: "Viti Levu Island",
      type: "Region",
    },
    {
      name: t("Destinations.BoraBora"),
      image: "/assets/v2/images/bora-bora-destination.jpg",
      description: t("Destinations.BoraBoraDescription"),
      hotels: 45,
      region: "Oceania",
      id: "C78142",
      country: "French Polynesia",
      city: "Bora Bora",
      type: "City",
    },
  ],

  // 7. MIDDLE EAST (6)
  middleEast: [
    {
      name: t("Destinations.Dubai"),
      image: "/assets/v2/images/dubai-destination.jpg",
      description: t("Destinations.DubaiDescription"),
      hotels: 300,
      region: "Middle East",
      id: "C66",
      country: "United Arab Emirates",
      city: "Dubai",
      type: "City",
    },
    {
      name: t("Destinations.Istanbul"),
      image: "/assets/v2/images/istanbul-destination.jpg",
      description: t("Destinations.IstanbulDescription"),
      hotels: 190,
      region: "Middle East",
      id: "C89117",
      country: "Turkey",
      city: "Istanbul",
      type: "City",
    },
    {
      name: t("Destinations.Jerusalem"),
      image: "/assets/v2/images/jerusalem-destination.jpg",
      description: t("Destinations.JerusalemDescription"),
      hotels: 110,
      region: "Middle East",
      id: "C57990",
      country: "Israel",
      city: "Jerusalem",
      type: "City",
    },
    {
      name: t("Destinations.Doha"),
      image: "/assets/v2/images/doha-destination.jpg",
      description: t("Destinations.DohaDescription"),
      hotels: 150,
      region: "Middle East",
      id: "C81766",
      country: "Qatar",
      city: "Doha",
      type: "City",
    },
    {
      name: t("Destinations.Petra"),
      image: "/assets/v2/images/petra-destination.jpg",
      description: t("Destinations.PetraDescription"),
      hotels: 40,
      region: "Middle East",
      id: "C61267",
      country: "Jordan",
      city: "Petra",
      type: "City",
    },
    {
      name: t("Destinations.AbuDhabi"),
      image: "/assets/v2/images/abu-dhabi-destination.jpg",
      description: t("Destinations.AbuDhabiDescription"),
      hotels: 180,
      region: "Middle East",
      id: "C3",
      country: "United Arab Emirates",
      city: "Abu Dhabi",
      type: "City",
    },
  ],
});

export const getDestinations = (
  t: ReturnType<typeof useTranslations>
): Destination[] => [
  {
    name: t("Destinations.AbuDhabi"),
    image: "/assets/v2/images/abu-dhabi-destination.jpg",
    description: t("Destinations.AbuDhabiDescription"),
    hotels: 180,
    region: "Middle East",
    id: "C3",
    country: "United Arab Emirates",
    city: "Abu Dhabi",
    type: "City",
  },
  {
    name: t("Destinations.Istanbul"),
    image: "/assets/v2/images/istanbul-destination.jpg",
    description: t("Destinations.IstanbulDescription"),
    hotels: 190,
    region: "Middle East",
    id: "C89117",
    country: "Turkey",
    city: "Istanbul",
    type: "City",
  },
  {
    name: t("Destinations.Dubai"),
    image: "/assets/v2/images/dubai-destination.jpg",
    description: t("Destinations.DubaiDescription"),
    hotels: 300,
    region: "Middle East",
    id: "C66",
    country: "United Arab Emirates",
    city: "Dubai",
    type: "City",
  },
  {
    name: t("Destinations.Queenstown"),
    image: "/assets/v2/images/queenstown-destination.jpg",
    description: t("Destinations.QueenstownDescription"),
    hotels: 85,
    region: "Oceania",
    id: "C78064",
    country: "New Zealand",
    city: "Queenstown",
    type: "City",
  },
  {
    name: t("Destinations.Brisbane"),
    image: "/assets/v2/images/brisbane-destination.jpg",
    description: t("Destinations.BrisbaneDescription"),
    hotels: 100,
    region: "Oceania",
    id: "C3223",
    country: "Australia",
    city: "Brisbane",
    type: "City",
  },
  {
    name: t("Destinations.Toronto"),
    image: "/assets/v2/images/toronto-destination.jpg",
    description: t("Destinations.TorontoDescription"),
    hotels: 150,
    region: "North America",
    id: "C11342",
    country: "Canada",
    city: "Toronto",
    type: "City",
  },
  {
    name: t("Destinations.NewYork"),
    image: "/assets/v2/images/new-york.jpg",
    description: t("Destinations.NewYorkDescription"),
    hotels: 400,
    region: "North America",
    id: "C96604",
    country: "USA",
    city: "New York",
    type: "City",
  },
  {
    name: t("Destinations.London"),
    image: "/assets/v2/images/london-destination.jpg",
    description: t("Destinations.LondonDescription"),
    hotels: 350,
    region: "Europe",
    id: "C35155",
    country: "United Kingdom",
    city: "London",
    type: "City",
  },
  {
    name: t("Destinations.Lisbon"),
    image: "/assets/v2/images/lisbon-destination.jpg",
    description: t("Destinations.LisbonDescription"),
    hotels: 105,
    region: "Europe",
    id: "C97303",
    country: "Portugal",
    city: "Lisbon",
    type: "City",
  },
  {
    name: t("Destinations.Barcelona"),
    image: "/assets/v2/images/barcelona-destination.jpg",
    description: t("Destinations.BarcelonaDescription"),
    hotels: 145,
    region: "Europe",
    id: "C27790",
    country: "Spain",
    city: "Barcelona",
    type: "City",
  },
];
